"use client";

import { useState } from "react";
import { ArrowRight } from "lucide-react";
import { useLocale, useTranslations } from "next-intl";
import { z } from "zod";
import { toast } from "sonner";
import axios, { AxiosError } from "axios";
import {
  convertArabicDigitsToEnglish,
  PhoneNumberInput,
} from "@/components/ui/phone-number-input";

// Mirrors the reference's phoneNumberValidation useMemo logic exactly.
// SA/UAE: length is first-char-aware — '0'-prefix numbers must be 10 digits,
// all others must be 9. KW/BH/QA/OM: exactly 8 (min and max enforced).
function validatePhone(
  val: string | undefined,
  dialCode: string,
  t: (k: string) => string,
): true | string {
  const isSaudiOrUAE = dialCode === "+966" || dialCode === "+971";

  if (isSaudiOrUAE) {
    if (!val) return t("phoneInvalid");
    const firstChar = convertArabicDigitsToEnglish(val[0]);
    const expectedLength = firstChar === "0" ? 10 : 9;
    return val.length === expectedLength || t("phoneInvalid");
  }

  const expectedLength = 8;
  if (!val || val.length < expectedLength) return t("phoneInvalid");
  if (val.length > expectedLength) return t("phoneInvalid");
  return true;
}

function buildSchema(t: (key: string) => string, dialCode: string) {
  return z.object({
    firstName: z
      .string()
      .min(2, t("firstNameMustBeAtLeastTwoCharacters"))
      .max(50, t("firstNameTooLong")),
    lastName: z
      .string()
      .min(2, t("lastNameMustBeAtLeastTwoCharacters"))
      .max(50, t("lastNameTooLong")),
    email: z
      .string()
      .min(1, t("emailRequired"))
      .email(t("emailInvalid")),
    company: z
      .string()
      .min(1, t("companyRequired"))
      .max(100, t("companyTooLong")),
    phone: z
      .string()
      .max(20, t("phoneNumberTooLong"))
      .optional()
      .refine(
        (val) => !val || validatePhone(val, dialCode, t) === true,
        (val) => ({
          message: val
            ? (validatePhone(val, dialCode, t) as string)
            : t("phoneInvalid"),
        }),
      ),
  });
}

function triggerPdfDownload(data: ArrayBuffer) {
  const blob = new Blob([data], { type: "application/pdf" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = "yamm-returns-benchmark-2025.pdf";
  link.click();
  URL.revokeObjectURL(url);
}

export function HeroForm() {
  const t = useTranslations("Hero.form");
  const locale = useLocale();

  const [language, setLanguage] = useState<"en" | "ar">(
    locale === "ar" ? "ar" : "en",
  );
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [company, setCompany] = useState("");
  const [phone, setPhone] = useState("");
  const [dialCode, setDialCode] = useState("+966");
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(false);

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();

    const result = buildSchema(t, dialCode).safeParse({
      firstName,
      lastName,
      email,
      company,
      phone,
    });

    if (!result.success) {
      const f = result.error.flatten().fieldErrors;
      setErrors({
        firstName: f.firstName?.[0] ?? "",
        lastName: f.lastName?.[0] ?? "",
        email: f.email?.[0] ?? "",
        company: f.company?.[0] ?? "",
        phone: f.phone?.[0] ?? "",
      });
      return;
    }

    setErrors({});
    setIsLoading(true);

    try {
      const res = await axios.post(
        `${process.env.NEXT_PUBLIC_BASE_URL}/api/report`,
        {
          first_name: firstName,
          last_name: lastName,
          email,
          company,
          language,
          ...(phone && { phone: `${dialCode}${phone}` }),
        },
        { responseType: "blob" },
      );

      triggerPdfDownload(res.data);
    } catch (err) {
      console.error("request failed:", err);
      const error = err as AxiosError<{ message?: string }>;
      toast.error(error.response?.data?.message ?? t("submitError"));
    } finally {
      setIsLoading(false);
    }
  }

  const inputBase =
    "w-full rounded-xl border px-4 py-3.5 text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:bg-white transition";

  const inputClass = (field: string) =>
    `${inputBase} ${
      errors[field]
        ? "border-red-400 bg-red-50 focus:ring-red-200 focus:border-red-400"
        : "border-slate-200 bg-slate-50 focus:ring-primary/25 focus:border-primary"
    }`;

  return (
    <form
      onSubmit={handleSubmit}
      className="space-y-2.5 max-w-sm [&_p]:text-wrap"
    >
      {/* First name + Last name */}
      <div className="grid grid-cols-2 gap-2.5">
        <div>
          <input
            type="text"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            placeholder={t("firstName")}
            className={inputClass("firstName")}
          />
          {errors.firstName && (
            <p className="mt-1.5 text-xs text-red-500">{errors.firstName}</p>
          )}
        </div>
        <div>
          <input
            type="text"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            placeholder={t("lastName")}
            className={inputClass("lastName")}
          />
          {errors.lastName && (
            <p className="mt-1.5 text-xs text-red-500">{errors.lastName}</p>
          )}
        </div>
      </div>

      {/* Work email */}
      <div>
        <input
          type="text"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder={t("email")}
          className={inputClass("email")}
        />
        {errors.email && (
          <p className="mt-1.5 text-xs text-red-500">{errors.email}</p>
        )}
      </div>

      {/* Company */}
      <div>
        <input
          type="text"
          value={company}
          onChange={(e) => setCompany(e.target.value)}
          placeholder={t("company")}
          className={inputClass("company")}
        />
        {errors.company && (
          <p className="mt-1.5 text-xs text-red-500">{errors.company}</p>
        )}
      </div>

      <div>
        <PhoneNumberInput
          value={phone}
          onChange={setPhone}
          dialCode={dialCode}
          onDialCodeChange={setDialCode}
          error={errors.phone}
          placeholder={t("phone")}
        />
        {errors.phone && (
          <p className="mt-1.5 text-xs text-red-500">{errors.phone}</p>
        )}
      </div>

      <div className="flex rounded-xl border border-slate-200 bg-slate-50 p-1 gap-1 h-13">
        <button
          type="button"
          onClick={() => setLanguage("en")}
          className={`flex-1 rounded-lg py-2.5 text-sm font-semibold transition-all font-display ${
            language === "en"
              ? "bg-white text-primary shadow-sm border border-slate-200"
              : "text-slate-500 hover:text-slate-700"
          }`}
        >
          English
        </button>
        <button
          type="button"
          onClick={() => setLanguage("ar")}
          className={`flex-1 rounded-lg py-2.5 text-sm font-semibold transition-all font-arabic ${
            language === "ar"
              ? "bg-white text-primary shadow-sm border border-slate-200"
              : "text-slate-500 hover:text-slate-700"
          }`}
        >
          العربية
        </button>
      </div>
      <button
        type="submit"
        disabled={isLoading}
        className="w-full flex items-center justify-center gap-2 rounded-xl bg-primary px-6 py-3.5 text-sm font-bold text-white hover:bg-primary/90 active:scale-[0.99] transition-all disabled:opacity-60 disabled:cursor-not-allowed"
      >
        {t("btn")}
        <ArrowRight className="h-4 w-4 rtl:rotate-180" />
      </button>
    </form>
  );
}
